import { useState } from "react";
import { Sparkles, Loader2, Check } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { useAuth } from "@/context/AuthContext";

export const AIAssistPanel = ({ recordType, attachments = [], onApply, canChangeType }) => {
  const { meta } = useAuth();
  const [text, setText] = useState("");
  const [attId, setAttId] = useState("");
  const [busy, setBusy] = useState(false);
  const [s, setS] = useState(null);
  const [pick, setPick] = useState({});
  const readable = attachments.filter((a) => a.state === "current" && /\.(pdf|txt|csv)$/i.test(a.filename));

  const analyse = async () => {
    setBusy(true);
    try {
      const r = await api.post("/ai/analyze", { text, attachment_id: attId || null, record_type: recordType || null });
      setS(r.data);
      const keys = { title: !!r.data.title, tags: r.data.tags?.length > 0, summary: !!r.data.summary,
        type: canChangeType && r.data.record_type && r.data.record_type !== recordType };
      Object.keys(r.data.fields || {}).forEach((k) => (keys[`f_${k}`] = true));
      setPick(keys);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const apply = () => {
    const fields = {};
    Object.entries(s.fields || {}).forEach(([k, v]) => pick[`f_${k}`] && (fields[k] = v));
    onApply({ record_type: pick.type ? s.record_type : null, title: pick.title ? s.title : null,
      tags: pick.tags ? s.tags : null, summary: pick.summary ? s.summary : null, fields });
    toast.success("Suggestions applied — review before saving");
    setS(null);
  };

  const Row = ({ k, label, children }) => (
    <label className="flex gap-2 items-start py-2 border-b border-slate-100 last:border-0 cursor-pointer">
      <Checkbox checked={!!pick[k]} onCheckedChange={(v) => setPick({ ...pick, [k]: !!v })} data-testid={`ai-pick-${k}`} className="mt-0.5" />
      <div className="min-w-0"><div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">{label}</div><div className="text-sm text-slate-800 break-words">{children}</div></div>
    </label>
  );

  return (
    <div className="rounded-lg border border-slate-200 bg-white shadow-sm" data-testid="ai-assist-panel">
      <div className="px-4 py-3 border-b border-slate-200 flex items-center gap-2">
        <Sparkles className="w-4 h-4 text-amber-600" />
        <div className="font-display font-semibold text-sm">AI Assist</div>
        <span className="ml-auto text-[10px] uppercase tracking-wider text-slate-400">Suggestions only</span>
      </div>
      <div className="p-4 space-y-3">
        {readable.length > 0 && (
          <select value={attId} onChange={(e) => setAttId(e.target.value)} data-testid="ai-attachment-select"
            className="w-full h-9 rounded-md border border-slate-300 bg-white text-sm px-2">
            <option value="">Read from pasted text</option>
            {readable.map((a) => <option key={a.attachment_id} value={a.attachment_id}>Read: {a.filename}</option>)}
          </select>
        )}
        <Textarea rows={5} value={text} onChange={(e) => setText(e.target.value)} data-testid="ai-text-input"
          placeholder="Paste the letter, minutes, invoice or complaint text here…" className="text-sm" />
        <Button onClick={analyse} disabled={busy} className="w-full" data-testid="ai-extract-fields-button">
          {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Sparkles className="w-4 h-4 mr-2" />}Classify & extract
        </Button>
        {s && (
          <div className="fade-up rounded-md bg-amber-50/60 border border-amber-200 p-3" data-testid="ai-suggestions">
            {s.record_type && (
              <div className="text-xs text-slate-600 mb-2">
                Looks like <b>{meta.record_types[s.record_type]?.label}</b>
                {s.confidence != null && <> ({Math.round(s.confidence * 100)}% confidence)</>}
              </div>
            )}
            {canChangeType && s.record_type && s.record_type !== recordType && <Row k="type" label="Record type">{meta.record_types[s.record_type]?.label}</Row>}
            {s.title && <Row k="title" label="Title">{s.title}</Row>}
            {Object.entries(s.fields || {}).map(([k, v]) => <Row key={k} k={`f_${k}`} label={k.replace(/_/g, " ")}>{String(v)}</Row>)}
            {s.tags?.length > 0 && <Row k="tags" label="Tags">{s.tags.join(", ")}</Row>}
            {s.summary && <Row k="summary" label="Summary">{s.summary}</Row>}
            <Button onClick={apply} size="sm" className="w-full mt-3 bg-amber-600 hover:bg-amber-700" data-testid="ai-confirm-apply-button">
              <Check className="w-4 h-4 mr-1" />Apply selected
            </Button>
          </div>
        )}
        <p className="text-[11px] text-slate-500 leading-relaxed">AI never saves, submits, routes, approves or deletes. You confirm every change.</p>
      </div>
    </div>
  );
};
